import { Box, Card, CardActionArea, Fade, Grid, Stack, Typography } from "@mui/material";
import { Videocam, MedicalServices, Chat, HomeWork } from "@mui/icons-material";
import { isMobile } from "react-device-detect";

interface IProps {
  onSelectModel: (value: string) => void;
}

const ATTENDANCE_OPTIONS = [
  {
    value: "physicalLocation",
    label: "Presencial",
    description: "Agende uma consulta para ir até uma clínica, consultório ou unidade de saúde próxima.",
    icon: MedicalServices,
    lightKey: "primary100", 
    darkKey:  "primary800", 
  },
  {
    value: "virtualService",
    label: "Teleatendimento",
    description: "Realize sua consulta online por vídeo chamada, no conforto de onde estiver.",
    icon: Videocam,
    lightKey: "secondary100", 
    darkKey:  "secondary700",
  },
  {
    value: "chatService",
    label: "Chat",
    description: "Converse com o profissional de saúde por mensagens de texto em tempo real.",
    icon: Chat,
    lightKey: "warning100", 
    darkKey:  "warning700", 
  },
  {
    value: "domicilioService",
    label: "Atendimento a Domicílio",
    description: "Receba o profissional de saúde no conforto da sua residência.",
    icon: HomeWork,
    lightKey: "success100",
    darkKey:  "success800", 
  },
];

export function ModelSelectionStep({ onSelectModel }: IProps) {
  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ py: isMobile ? 2 : 6, maxWidth: 1100, mx: "auto" }}>
        <Stack spacing={1} alignItems="center" mb={isMobile ? 4 : 6}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            align="center"
            fontWeight="bold"
            color="text.primary"
            sx={{ mb: 1 }}
          >
            Como você prefere ser atendido?
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 2 }}
          >
            Escolha a modalidade que melhor se adapta à sua necessidade de saúde hoje.
          </Typography>
        </Stack>

        <Grid container spacing={isMobile ? 2 : 3} justifyContent="center" alignItems="stretch">
          {ATTENDANCE_OPTIONS.map((option) => {
            const IconComponent = option.icon;
            return (
              <Grid key={option.value} item xs={12} sm={6} md={3}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    border: "2px solid transparent",
                    borderRadius: 4,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      // Borda com a cor escura da opção — resolvida via theme callback
                      borderColor: (theme: any) => theme.palette[option.darkKey].main,
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => onSelectModel(option.value)}
                    sx={{
                      p: isMobile ? 3 : 4,
                      height: "100%",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        // Gradiente usando as chaves do Theme.tsx via theme callback
                        background: (theme: any) =>
                          `linear-gradient(135deg, ${theme.palette[option.lightKey].main} 0%, ${theme.palette[option.darkKey].main}20 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        mx: "auto",
                        // Cor do ícone via theme callback
                        color: (theme: any) => theme.palette[option.darkKey].main,
                        transition: "transform 0.3s ease",
                        ".MuiCard-root:hover &": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <IconComponent sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                      {option.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                      {option.description}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Fade>
  );
}
